import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import Sidebar from "@/components/Sidebar";
import ContextBar from "@/components/ContextBar";
import StructurePanel from "@/components/StructurePanel";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, LogOut, ChevronDown, Sun, Moon } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [panelOpen, setPanelOpen] = useState(false);
  const { data: version } = useQuery({ queryKey: ["system-version"], queryFn: async () => (await api.get("/params/system-version")).data, staleTime: 60000 });
  useEffect(() => {
    const open = () => setPanelOpen(true);
    window.addEventListener("zae:open-structure", open);
    return () => window.removeEventListener("zae:open-structure", open);
  }, []);
  const [mobileNav, setMobileNav] = useState(false);
  const [treePinnedPref, setTreePinned] = useState(() => localStorage.getItem("zae_tree_pinned") === "1");
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 1536px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1536px)");
    const on = () => setWide(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const treePinned = treePinnedPref && wide;
  const toggleTreePin = () => setTreePinned((p) => { const n = !p; localStorage.setItem("zae_tree_pinned", n ? "1" : "0"); if (n) setPanelOpen(false); return n; });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#090D16]">
      <header className="sticky top-0 z-40 border-b border-[#1c2740] bg-[#090D16]/95 backdrop-blur-xl">
        <div className="h-16 px-2 sm:px-5 flex items-center gap-1.5 sm:gap-4">
          <button onClick={() => setMobileNav(true)} data-testid="mobile-nav-toggle" title="Menú"
            className="xl:hidden w-9 h-9 rounded-lg flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/5 shrink-0">
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden sm:flex items-center gap-2.5 shrink-0">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-lg shadow-red-900/30 p-1">
              <img src="/logo-zae.png" alt="ZAE Ejecutivo" className="w-full h-full object-contain" />
            </div>
            <div className="leading-tight hidden md:block">
              <p className="font-display font-extrabold text-white text-sm tracking-tight">Grupo Tomza</p>
              <p className="text-[10px] uppercase tracking-widest text-red-500 font-semibold -mt-0.5">ZAE Ejecutivo</p>
            </div>
          </div>

          <div className="h-8 w-px bg-[#223048] hidden xl:block" />

          <div className="flex-1 min-w-0">
            <ContextBar />
          </div>

          <button onClick={toggle} data-testid="theme-toggle" title={theme === "light" ? "Modo oscuro" : "Modo claro"}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5 transition-colors shrink-0">
            {theme === "light" ? <Moon className="w-4.5 h-4.5" /> : <Sun className="w-4.5 h-4.5" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/5 transition-colors shrink-0" data-testid="user-menu-trigger">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-xs font-bold text-white">
                  {(user?.name || "U").charAt(0).toUpperCase()}
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-[#131C2E] border-[#223048] text-slate-200">
              <DropdownMenuLabel>
                <p className="font-semibold text-white">{user?.name}</p>
                <p className="text-xs text-slate-400 font-normal">{user?.email}</p>
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {(user?.roles || []).map((r) => (
                    <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-red-600/15 text-red-400">{r}</span>
                  ))}
                </div>
                {version?.version && <p className="mt-1.5 text-[10px] font-normal text-slate-500" data-testid="user-menu-version">Versión {version.version}</p>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#223048]" />
              <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:text-red-400 focus:bg-red-600/10 cursor-pointer" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex relative">
        <div className="hidden xl:block">
          <Sidebar onOpenTree={() => setPanelOpen(true)} treePinned={treePinned} />
        </div>
        {treePinned && (
          <StructurePanel pinned open onClose={() => {}} onTogglePin={toggleTreePin} />
        )}
        {mobileNav && (
          <>
            <div className="fixed inset-0 top-16 bg-black/60 z-40 xl:hidden" onClick={() => setMobileNav(false)} data-testid="mobile-nav-overlay" />
            <div className="fixed left-0 top-16 bottom-0 z-50 xl:hidden">
              <Sidebar mobile onNavigate={() => setMobileNav(false)} onOpenTree={() => { setMobileNav(false); setPanelOpen(true); }} />
            </div>
          </>
        )}
        <main className="flex-1 min-w-0 px-3 sm:px-6 py-4 sm:py-6">{children}</main>
      </div>

      <StructurePanel open={panelOpen} onClose={() => setPanelOpen(false)} pinned={false} onTogglePin={toggleTreePin} />
    </div>
  );
}
